import moment from 'moment';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { first, values } from 'lodash';

import {
    CHANGE_AVAILABILITY_TIME_FILTER,
    CHANGE_DATE,
    CHANGE_DOCTOR_LOCATION_IDS,
    CHANGE_IS_EXISTING_PATIENT,
    CHANGE_PROCEDURE,
    CHANGE_SELECTED_TIMESLOT,
} from '../constants/TimesGridStateStoreEvents';

import {
    LOAD_TIMES,
    RESET,
} from '../constants/AppointmentTimesStoreEvents';

import TimesGridActions from '../actions/TimesGridActions';
import TimesGridStateStore from '../stores/TimesGridStateStore';
import getDateRange from '../utils/getDateRange';

/**
 *
 * @param config
 * @param styles
 * @return {Function}
 * @constructor
 */
export default function CreateTimesGridControls(config) {
    return function wrapComponent(WrappedComponent) {
        class BaseTimesGridControls extends PureComponent {
            static propTypes = {
                // Required Parameters
                gridActions: PropTypes.instanceOf(TimesGridActions).isRequired,
                gridStateStore: PropTypes.instanceOf(TimesGridStateStore).isRequired,
            }

            constructor(...args) {
                super(...args);

                this.state = this.getTimesGridControlsState();
            }

            componentDidMount() {
                // Any events added here should be unbound in componentWillUnmount
                this.props.gridStateStore.on(CHANGE_AVAILABILITY_TIME_FILTER, this.onChange);
                this.props.gridStateStore.on(CHANGE_PROCEDURE, this.onChange);
                this.props.gridStateStore.on(CHANGE_IS_EXISTING_PATIENT, this.handleIsExistingPatientChange);
                this.props.gridStateStore.on(CHANGE_DOCTOR_LOCATION_IDS, this.onChange);
                this.props.gridStateStore.on(CHANGE_SELECTED_TIMESLOT, this.onChange);
                this.props.gridStateStore.on(CHANGE_DATE, this.onChange);

                this.bindTimesStoreEventHandlers();
            }

            componentDidUpdate(prevProps) {
                if (prevProps.isExistingPatient !== this.props.isExistingPatient) {
                    this.handleIsExistingPatientChange();
                }
            }

            componentWillUnmount() {
                this.props.gridStateStore.off(CHANGE_AVAILABILITY_TIME_FILTER, this.onChange);
                this.props.gridStateStore.off(CHANGE_PROCEDURE, this.onChange);
                this.props.gridStateStore.off(CHANGE_IS_EXISTING_PATIENT, this.handleIsExistingPatientChange);
                this.props.gridStateStore.off(CHANGE_DOCTOR_LOCATION_IDS, this.onChange);
                this.props.gridStateStore.off(CHANGE_SELECTED_TIMESLOT, this.onChange);
                this.props.gridStateStore.off(CHANGE_DATE, this.onChange);

                this.unbindTimesStoreEventHandlers();
            }

            render() {
                return (
                    <WrappedComponent
                        nextBtnClicked={this.nextBtnClicked}
                        prevBtnClicked={this.prevBtnClicked}
                        {...this.props}
                        {...this.state}
                    />
                );
            }

            isFirstPossibleDateFrame(sortedDates) {
                return moment().dayDiff(sortedDates[0]) === 0;
            }

            getTimesGridControlsState() {
                const dateRange = getDateRange(
                    this.props.gridStateStore.getState().date, // current date
                    config.MAX_VISIBLE_DAYS
                );

                return {
                    disableNextArrow: this.isNextArrowDisabled(),
                    dates: dateRange,
                    isFirstPossibleDateFrame: this.isFirstPossibleDateFrame(dateRange),
                };
            }

            isNextArrowDisabled() {
                const { doctorLocationIds } = this.props;
                if (typeof doctorLocationIds === 'string' || (Array.isArray(doctorLocationIds) && doctorLocationIds.length === 1)) {
                    const doctorLocationId = this.props.gridStateStore.getState().doctorLocationIds[0];
                    const times = this.props.gridStateStore.timesStore.getByDoctorLocation(
                        doctorLocationId,
                        this.props.gridStateStore.getState().date, // current date
                        config.MAX_VISIBLE_DAYS
                    );

                    if (times.hasLoaded) {
                        const firstDateNextAppt = first(values(times.times)); // first date supplied
                        if (firstDateNextAppt && !firstDateNextAppt.nextAppointment) {
                            return true;
                        }
                    }
                }
                return false;
            }

            bindTimesStoreEventHandlers = () => {
                const { gridStateStore } = this.props;
                gridStateStore.timesStore.on(RESET, this.onChange);
                gridStateStore.timesStore.on(LOAD_TIMES, this.onChange);
            };

            unbindTimesStoreEventHandlers = () => {
                const { gridStateStore: { newPatientTimesStore, existingPatientTimesStore } } = this.props;
                // We don't know which times store (existing or new) was active, so remove listeners on both stores.
                [newPatientTimesStore, existingPatientTimesStore].forEach(timesStore => {
                    timesStore.off(RESET, this.onChange);
                    timesStore.off(LOAD_TIMES, this.onChange);
                });
            };

            prevBtnClicked = numColsVisible => {
                this.props.gridActions.changeNumDaysVisible(numColsVisible);
                this.props.gridActions.clickPrevDays();
                if (this.props.onClickPrev) this.props.onClickPrev();
            }

            nextBtnClicked = numColsVisible => {
                this.props.gridActions.changeNumDaysVisible(numColsVisible);
                this.props.gridActions.clickNextDays();
                if (this.props.onClickNext) this.props.onClickNext();
            }

            onChange = () => this.setState(this.getTimesGridControlsState())

            handleIsExistingPatientChange = e => {
                this.unbindTimesStoreEventHandlers();
                this.bindTimesStoreEventHandlers();
                this.onChange(e);
            };
        }

        return BaseTimesGridControls;
    };
}
